import { useQuery } from '@tanstack/react-query';
import { CONTEXT_SECTIONS, CONTEXT_SECTION_TITLES, type ContextSection } from '@lixionary-jira/domain';
import { BookOpen } from 'lucide-react';
import { useState } from 'react';
import { Link, NavLink, useParams } from 'react-router';
import { api, useAction, useProject } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import type { ContextSectionView, ContextVersionView } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Button } from '../../ui/Button';
import { DownloadPdfButton } from '../../ui/DownloadPdf';
import { Alert, EmptyState, ErrorAlert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Field, Input, Segmented, Textarea } from '../../ui/forms';
import { Markdown } from '../../ui/Markdown';
import { ActorName, Card, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

/** PRJ-05/06: versioned Markdown context with templates and missing-section indicators. */
export function ContextPage() {
  const { key, section } = useParams();
  const project = useProject(key);
  const valid = (CONTEXT_SECTIONS as readonly string[]).includes(section ?? '');
  const sec = (valid ? section : 'overview') as ContextSection;
  const title = CONTEXT_SECTION_TITLES[sec];
  usePageTitle(project.data ? `${project.data.name} · ${title}` : null);
  useCrumbs([
    { label: project.data?.name ?? key ?? '', to: `/projects/${key}` },
    { label: 'Context' },
    { label: title },
  ]);
  const admin = useIsAdmin();
  const q = useQuery({
    queryKey: ['context', key, sec],
    queryFn: () => api.get<ContextSectionView>(`/projects/${key}/context/${sec}`),
    enabled: valid,
  });
  const [editing, setEditing] = useState(false);
  if (!valid) return <PageUnavailable />;
  const canEdit = admin && project.data && !project.data.archived;
  return (
    <div className="page">
      <PageHeader
        title={title}
        subtitle="Project context is versioned. Implementations and releases record the versions they used."
      />
      <nav className="tabs" aria-label="Context sections">
        {CONTEXT_SECTIONS.map((s) => (
          <NavLink
            key={s}
            to={`/projects/${key}/context/${s}`}
            className="tab"
            onClick={() => setEditing(false)}
          >
            {CONTEXT_SECTION_TITLES[s]}
          </NavLink>
        ))}
      </nav>
      <QueryView query={q}>
        {(ctx) => (
          <div className="split">
            <div className="stack">
              {ctx.missing.length > 0 && !editing && (
                <Alert tone="warning" title={ctx.current ? 'Incomplete section' : 'Missing section'}>
                  <span>Add content under: {ctx.missing.join(', ')}.</span>
                </Alert>
              )}
              {editing ? (
                <ContextEditor projectKey={key!} ctx={ctx} onDone={() => setEditing(false)} />
              ) : ctx.current ? (
                <Card
                  title={`Version ${ctx.current.number}`}
                  subtitle={
                    <>
                      <ActorName actor={ctx.current.author} /> · <Timestamp value={ctx.current.createdAt} /> ·{' '}
                      {ctx.current.changeSummary}
                    </>
                  }
                  action={
                    <div className="row">
                      <ContextPdfButton
                        projectName={project.data?.name ?? key!}
                        sectionTitle={title}
                        version={ctx.current}
                      />
                      {canEdit && (
                        <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                          Edit
                        </Button>
                      )}
                    </div>
                  }
                >
                  <Markdown>{ctx.current.body}</Markdown>
                </Card>
              ) : (
                <Card>
                  <EmptyState
                    icon={BookOpen}
                    title={`No ${title.toLowerCase()} yet`}
                    action={canEdit && <Button onClick={() => setEditing(true)}>Start from template</Button>}
                  >
                    {canEdit
                      ? 'Start from the template; each heading is part of the completeness check.'
                      : 'An admin has not written this section yet.'}
                  </EmptyState>
                </Card>
              )}
            </div>
            <Card title="History" tight>
              {ctx.history.length === 0 ? (
                <span className="small muted">No versions yet.</span>
              ) : (
                <ol className="checklist">
                  {ctx.history.map((h) => (
                    <li key={h.id} style={{ flexDirection: 'column', gap: 2 }}>
                      <Link to={`/projects/${key}/context/${sec}/versions/${h.number}`} className="mono">
                        v{h.number}
                      </Link>
                      <span className="small">{h.changeSummary}</span>
                      <span className="small muted">
                        <ActorName actor={h.author} /> · <Timestamp value={h.createdAt} />
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </Card>
          </div>
        )}
      </QueryView>
    </div>
  );
}

/** Saves one context version as an A4 PDF, headed with the project, section and version details. */
export function ContextPdfButton({
  projectName,
  sectionTitle,
  version,
}: {
  projectName: string;
  sectionTitle: string;
  version: ContextVersionView;
}) {
  return (
    <DownloadPdfButton
      fileName={`${projectName} - ${sectionTitle} v${version.number}`}
      title={`${projectName} · ${sectionTitle}`}
      details={
        <>
          Version {version.number} · <ActorName actor={version.author} /> ·{' '}
          <Timestamp value={version.createdAt} /> · {version.changeSummary}
        </>
      }
      body={version.body}
    />
  );
}

function ContextEditor({
  projectKey,
  ctx,
  onDone,
}: {
  projectKey: string;
  ctx: ContextSectionView;
  onDone: () => void;
}) {
  const [body, setBody] = useState(ctx.current?.body ?? ctx.template);
  const [summary, setSummary] = useState('');
  const [mode, setMode] = useState<'write' | 'preview'>('write');
  const save = useAction(
    () =>
      api.post(`/projects/${projectKey}/context/${ctx.section}`, {
        body,
        changeSummary: summary,
        expectedCurrentVersionId: ctx.current?.id ?? null,
      }),
    {
      success: 'Saved new version',
      invalidate: [
        ['context', projectKey],
        ['project', projectKey],
      ],
      onSuccess: onDone,
    },
  );
  return (
    <Card
      title={`Edit ${ctx.title}`}
      subtitle="Saving creates a new immutable version. Never paste secret values; reference where they live instead."
    >
      <Segmented
        label="Editor mode"
        value={mode}
        onChange={setMode}
        options={[
          { value: 'write', label: 'Write' },
          { value: 'preview', label: 'Preview' },
        ]}
      />
      {mode === 'write' ? (
        <Field label="Markdown">
          {(p) => <Textarea {...p} mono value={body} onChange={(e) => setBody(e.target.value)} rows={22} />}
        </Field>
      ) : (
        <div className="well">
          <Markdown>{body}</Markdown>
        </div>
      )}
      <Field label="Change summary" hint="Why this version exists. Shown in history and audit.">
        {(p) => (
          <Input
            {...p}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="e.g. Clarify offline guarantees"
            maxLength={500}
          />
        )}
      </Field>
      {ctx.section === 'deployment' && (
        <Alert tone="info">
          Saving deployment instructions invalidates outstanding release approvals in this project.
        </Alert>
      )}
      <ErrorAlert error={save.error} />
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button onClick={() => save.mutate(undefined)} loading={save.isPending} disabled={!summary.trim()}>
          Save version
        </Button>
      </div>
    </Card>
  );
}
